import { type Checkout, CheckoutStatus, type Permission, ReservationStatus } from '@cheqroom/api';
import { DateTime } from '@cheqroom/date-time';
import { useMemo } from 'react';

import { type Props as PermissionsProps, usePermissions } from './use-permissions.hook';
import { createSpotcheckPermissions } from './use-spotcheck-permissions.hook';

interface Options {
	isExtending?: boolean;
	own?: boolean;
}

interface FeatureFlags {
	allowPastBookings?: boolean;
}

type UseCheckoutPermissionsProps = PermissionsProps & FeatureFlags;

const MINIMUM_DATETIME_FOR_CONSIDERED_IN_PAST = { hours: 1 };

export const createCheckoutPermissions = (
	permissions: ReturnType<typeof usePermissions>,
	featureFlags?: FeatureFlags
) => {
	const spotcheckPermissions = createSpotcheckPermissions(permissions);

	const { allowPastBookings = false } = featureFlags ?? {};
	const {
		hasAnyPermission: _hasAnyPermission,
		useCheckouts,
		orderCheckinLocation,
		usePastOrders,
		useBlockEditOfOpenOrders,
		usePdf,
		useSpotchecks,
		canUpgrade,
	} = permissions;

	const hasAnyPermission = (...required: Permission[]) => useCheckouts && _hasAnyPermission(...required);

	const canWriter = (own?: boolean) =>
		hasAnyPermission('ORDERS_WRITER', 'ORDERS_WRITER_RESTRICTED') || (own && hasAnyPermission('ORDERS_OWN_WRITER'));

	const canArchive = (own?: boolean) => {
		return (
			hasAnyPermission('ORDERS_ARCHIVER', 'ORDERS_ARCHIVER_RESTRICTED') ||
			(own && hasAnyPermission('ORDERS_OWN_ARCHIVER'))
		);
	};

	const canLabel = (own?: boolean) => {
		return (
			hasAnyPermission('ORDERS_LABELER', 'ORDERS_LABELER_RESTRICTED') ||
			(own && hasAnyPermission('ORDERS_OWN_LABELER'))
		);
	};

	const checkoutPermissions = {
		canCreate: () => canWriter(true),
		canCheckout: canWriter,
		// Picking who a check-out is for (`setCustomer`): the own tier never covers somebody else.
		canCheckoutForSomeoneElse: (): boolean => !!canWriter(false),
		canUpdate: canWriter,
		canRead: (own?: boolean) => {
			return (
				hasAnyPermission('ORDERS_READER', 'ORDERS_READER_RESTRICTED') ||
				(own && hasAnyPermission('ORDERS_OWN_READER'))
			);
		},
		canOverrideConflicts: () => hasAnyPermission('ORDERS_CONFLICT_CREATOR'),
		canCheckin: (checkout: Pick<Checkout, 'status'>, own?: boolean) => {
			if (checkout.status !== CheckoutStatus.OPEN) return false;
			return (
				hasAnyPermission('ORDERS_CHECKINNER', 'ORDERS_CHECKINNER_RESTRICTED') ||
				(own && hasAnyPermission('ORDERS_OWN_CHECKINNER'))
			);
		},
		canExtendCheckout: (checkout: Pick<Checkout, 'status'>, own?: boolean) => {
			if (checkout.status !== CheckoutStatus.OPEN) return false;
			return (
				hasAnyPermission('ORDERS_EXTENDER', 'ORDERS_EXTENDER_RESTRICTED') ||
				(own && hasAnyPermission('ORDERS_OWN_EXTENDER'))
			);
		},
		canCheckinAtDifferentLocation: (checkout: Pick<Checkout, 'status'>, own?: boolean) => {
			if (!orderCheckinLocation) return false;
			return checkoutPermissions.canCheckin(checkout, own);
		},
		canSelectEquipment: (checkout: Pick<Checkout, 'status'>, options: Options) => {
			const isDraft = checkout.status === CheckoutStatus.CREATING;
			const isOpen = checkout.status === CheckoutStatus.OPEN;
			return (isDraft || isOpen) && !options.isExtending && canWriter(options.own);
		},
		canWritePast: (own?: boolean) => {
			if (!usePastOrders || !allowPastBookings) return false;
			return hasAnyPermission('ORDERS_WRITER_PAST') && canWriter(own);
		},
		canUndoFutureBooking: (checkout: Pick<Checkout, 'status'>, own?: boolean) => {
			if (checkout.status !== CheckoutStatus.OPEN) return false;
			return canWriter(own);
		},
		canUndoPastBooking: (checkout: Pick<Checkout, 'status'>, own?: boolean) => {
			const canUndoBooking = checkoutPermissions.canUndoFutureBooking(checkout, own);
			return canUndoBooking && checkoutPermissions.canWritePast(own);
		},
		canUndoCurrentBooking: (checkout: Pick<Checkout, 'status' | 'started'>, own?: boolean) => {
			if (checkout.status !== CheckoutStatus.OPEN) return false;
			// Check if user has permission to undo bookings
			if (!canWriter(own)) return false;

			// If booking hasn't started yet (not in past), can always undo
			const isFromDateInPast =
				checkout.started &&
				DateTime.fromISOString(checkout.started).isBefore(
					DateTime.now().subtract(MINIMUM_DATETIME_FOR_CONSIDERED_IN_PAST)
				);
			if (!isFromDateInPast) {
				return true;
			}
			// For bookings that have already started (in past):
			// If past bookings are disabled OR blocking is disabled, always allow undo
			const usePastBookings = !!usePastOrders;
			const blockEditOfOpenBookings = !!useBlockEditOfOpenOrders;
			const alwaysAllowUndo = !usePastBookings || !blockEditOfOpenBookings;
			if (alwaysAllowUndo) {
				return true;
			}
			// If past bookings are enabled AND blocking is enabled:
			// Need write-past permission to undo past bookings
			return checkoutPermissions.canWritePast(own);
		},
		canEdit: (checkout: Pick<Checkout, 'status'>, own?: boolean) => {
			if (checkout.status !== CheckoutStatus.OPEN) return false;
			return canWriter(own);
		},
		canEditFields: (checkout: Pick<Checkout, 'archived'>, own?: boolean) => {
			if (checkout.archived) return false;
			return canWriter(own);
		},
		canDelete: (checkout: Pick<Checkout, 'status'>, own?: boolean) => {
			if (checkout.status !== CheckoutStatus.CREATING) return false;
			return canWriter(own);
		},
		canBookAgain: canWriter,
		canRepeatBooking: canWriter,
		canArchive: (checkout: Pick<Checkout, 'status' | 'archived'>, own?: boolean) => {
			if (checkout.status !== CheckoutStatus.CLOSED) return false;
			if (checkout.archived) return false;
			return canArchive(own);
		},
		canUndoArchive: (checkout: Pick<Checkout, 'archived'>, own?: boolean) => {
			if (!checkout.archived) return false;
			return canArchive(own);
		},
		canGenerateDocument: (own?: boolean) => {
			if (!usePdf) return false;
			return (
				hasAnyPermission('ORDERS_DOCUMENT_GENERATOR', 'ORDERS_DOCUMENT_GENERATOR_RESTRICTED') ||
				(own && hasAnyPermission('ORDERS_OWN_DOCUMENT_GENERATOR'))
			);
		},
		canUpgradePlan: canUpgrade,
		canCreateSpotcheck: () => {
			if (!useSpotchecks) return false;
			return spotcheckPermissions.canCreate();
		},
		canReadBooking: (own?: boolean) => checkoutPermissions.canRead(own),
		canSetLabel: (checkout: Pick<Checkout, 'archived'>, own?: boolean) => {
			if (checkout.archived) return false;
			return canLabel(own);
		},
		canClearLabel: (checkout: Pick<Checkout, 'archived'>, own?: boolean) => {
			if (checkout.archived) return false;
			return canLabel(own);
		},
	};

	return checkoutPermissions;
};

export const useCheckoutPermissions = ({ allowPastBookings = false, ...props }: UseCheckoutPermissionsProps) => {
	const permissions = usePermissions(props);

	return useMemo(
		() => createCheckoutPermissions(permissions, { allowPastBookings }),
		[permissions, allowPastBookings]
	);
};
